import { Chip, ChipProps } from '@mui/material';
import { alpha, darken } from '@mui/material/styles';
import { readableColor } from 'polished';

/** Slightly darkens raw pastel hex/CSS colors for better contrast on the chip. */
const DARKEN_COEFFICIENT = 0.12;

export type CustomChipProps = Omit<ChipProps, 'color'> & {
  /** Accent color (hex or CSS color). Outlined: border; filled: border and background. */
  chipColor: string;
};

/**
 * MUI Chip with an arbitrary accent color (not a theme palette `color` key).
 * Colors are darkened slightly for readability. Outlined: border and label match; filled: fill + border.
 */
export function CustomChip({ chipColor, variant = 'outlined', sx, ...rest }: CustomChipProps) {
  const accent = darken(chipColor, DARKEN_COEFFICIENT);
  const hoverAccent = darken(accent, 0.08);
  const pressedAccent = darken(accent, 0.14);

  const baseSx = {
    borderColor: accent,
    color: variant === 'outlined' ? accent : readableColor(accent),
    ...(variant === 'filled' ? { bgcolor: accent } : {}),
    '&.MuiChip-clickable:hover': {
      borderColor: hoverAccent,
      ...(variant === 'filled'
        ? { bgcolor: hoverAccent, color: readableColor(hoverAccent) }
        : {
            bgcolor: alpha(accent, 0.12),
          }),
    },
    '&.MuiChip-clickable:active': {
      borderColor: pressedAccent,
      ...(variant === 'filled'
        ? { bgcolor: pressedAccent, color: readableColor(pressedAccent) }
        : {
            bgcolor: alpha(accent, 0.2),
          }),
    },
    '&.Mui-focusVisible': {
      borderColor: hoverAccent,
      ...(variant === 'filled'
        ? { bgcolor: hoverAccent, color: readableColor(hoverAccent) }
        : {
            bgcolor: alpha(accent, 0.12),
          }),
    },
  };

  return (
    <Chip
      color="default"
      variant={variant}
      sx={sx == null ? baseSx : [baseSx, ...(Array.isArray(sx) ? sx : [sx])]}
      {...rest}
    />
  );
}
